import { useState } from "react";
import { supabase, db } from "@/lib/supabase";
import { User } from "@/store/slices/authSlice";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { useLanguage } from "./LanguageContext";

// Clean icon components
const EyeIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-gray-400"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

const EyeOffIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-gray-400"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
    <line x1="1" y1="1" x2="23" y2="23"></line>
  </svg>
);

const TrendingUpIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="22,7 13.5,15.5 8.5,10.5 2,17"></polyline>
    <polyline points="16,7 22,7 22,13"></polyline>
  </svg>
);

const UserIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

type UserType = "investor" | "admin";

interface LoginFormProps {
  onLoginSuccess?: (user: User) => void;
}

export function LoginForm({ onLoginSuccess }: LoginFormProps = {}) {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [] = useState<UserType>("investor");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [resetCooldownMs, setResetCooldownMs] = useState<number>(0);
  const [showResetModal, setShowResetModal] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const { t } = useLanguage();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsLoading(true);

    try {
      // Use Supabase Auth to sign in with email and password
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Sign-in error (invalid credentials, etc.)
        console.error("Supabase signIn error:", error);
        setErrorMessage(error.message || "Error al iniciar sesión");
        setIsLoading(false);
        return;
      }

      setIsLoading(false);

      // If a callback was provided, notify the parent with the authenticated user
      if (data?.user) {
        onLoginSuccess?.(data.user as unknown as User);
      }
    } catch (err) {
      console.error("Unexpected login error:", err);
      setErrorMessage("Error inesperado al iniciar sesión");
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full space-y-6">
      <Card
        className="rounded-2xl border border-gray-100 shadow-sm py-6"
        style={{ backgroundColor: "#fafafa" }}
      >
        <CardHeader className="space-y-8 pb-8 pt-4">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3">
            <div className="bg-white border border-border rounded-lg p-2">
              <TrendingUpIcon />
            </div>
            <span
              className="text-2xl text-gray-900"
              style={{ fontWeight: 500 }}
            >
              Bellamy & Partners
            </span>
          </div>

          {/* Title and Description */}
          <div className="text-center space-y-3">
            <CardTitle
              className="text-2xl text-gray-900 flex items-center justify-center gap-2"
              style={{ fontWeight: 500 }}
            >
              <UserIcon />
              {t("login.title")}
            </CardTitle>
            <CardDescription className="text-base text-gray-600">
              {t("login.tagline")}
            </CardDescription>
          </div>

          {/* Role Selector */}
          {/* <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setUserType("investor")}
              className={`
                p-4 rounded-lg border-2 transition-all duration-200
                ${
                  userType === "investor"
                    ? "border-[#2B59FF] bg-[#2B59FF]/5"
                    : "border-gray-200 hover:border-gray-300"
                }
              `}
            >
              <div className="flex flex-col items-center gap-2">
                <UserIcon />
                <span
                  className={`text-base ${
                    userType === "investor" ? "text-[#2B59FF]" : "text-gray-700"
                  }`}
                  style={{ fontWeight: 500 }}
                >
                  Socio
                </span>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setUserType("admin")}
              className={`
                p-4 rounded-lg border-2 transition-all duration-200
                ${
                  userType === "admin"
                    ? "border-[#2B59FF] bg-[#2B59FF]/5"
                    : "border-gray-200 hover:border-gray-300"
                }
              `}
            >
              <div className="flex flex-col items-center gap-2">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                  <path d="M2 17l10 5 10-5"></path>
                  <path d="M2 12l10 5 10-5"></path>
                </svg>
                <span
                  className={`text-base ${
                    userType === "admin" ? "text-[#2B59FF]" : "text-gray-700"
                  }`}
                  style={{ fontWeight: 500 }}
                >
                  Administrador
                </span>
              </div>
            </button>
          </div> */}
        </CardHeader>

        <CardContent className="space-y-8 px-6 pb-8">
          <form className="space-y-4" onSubmit={handleSubmit}>
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700 text-base">
                {t("login.email")}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="socio@ejemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-14 bg-white border-0 rounded-full mt-2 shadow-sm focus-visible:shadow-[inset_0_0_0_2px_rgba(226,232,240,1)] text-base"
                required
              />
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700 text-base">
                {t("login.password")}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Tu contraseña de socio"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-14 bg-white border-0 rounded-full mt-2 shadow-sm focus-visible:shadow-[inset_0_0_0_2px_rgba(226,232,240,1)] pr-10 text-base"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </Button>
              </div>
            </div>

            {/* Forgot password: open informational modal; hide remember-me */}
            <div>
              <Button
                type="button"
                variant="link"
                className="px-0 text-base text-black hover:text-gray-700"
                onClick={() => {
                  setErrorMessage(null);
                  setResetMessage(null);
                  setShowResetModal(true);
                }}
              >
                {t("login.forgotPassword")}
              </Button>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full h-14 bg-black hover:bg-gray-800 text-white font-medium rounded-lg text-base"
              style={{ backgroundColor: "#000000", color: "#ffffff" }}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                t("login.button")
              )}
            </Button>
          </form>

          {errorMessage && (
            <div className="mt-3 text-sm text-red-600 text-center">
              {errorMessage}
            </div>
          )}
          {resetMessage && (
            <div className="mt-3 text-sm text-green-600 text-center">
              {resetMessage}
            </div>
          )}

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="px-2 text-gray-500" style={{ backgroundColor: "#fafafa" }}>
                {t("login.or")}
              </span>
            </div>
          </div>

          {/* Help Text */}
          <div className="text-center text-sm text-gray-600">
            {t("login.support")}
          </div>
        </CardContent>
      </Card>

      {/* Bottom Security Text */}
      <div className="text-center text-xs text-gray-500"></div>
      {showResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h2 className="text-xl font-medium text-gray-900 mb-2">
              {t("login.forgotPassword")}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Para recuperar tu contraseña debes contactar con el administrador del sistema.
              Por favor, envía un correo a soporte o contacta internamente para que el administrador
              pueda restablecer tu acceso.
            </p>
            <div className="mt-6 flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setShowResetModal(false);
                }}
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
